"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  id: 1,
  name: "Sarah Mitchell",
  role: "CEO, TechStart Inc.",
  photo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1477031/img/client_sarah_mitchell.png",
  quote: "Restore and Hope LLC transformed our operational framework. Their strategic insights and personalized workshops gave us the clarity we needed to scale efficiently."
}, {
  id: 2,
  name: "David Chen",
  role: "Founder, GrowthWorks",
  photo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1477031/img/client_david_chen.png",
  quote: "The financial management guidance we received was invaluable. We've seen measurable improvements in our cash flow and long-term planning capabilities."
}, {
  id: 3,
  name: "Maria Rodriguez",
  role: "Operations Director, Summit Solutions",
  photo: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1477031/img/client_maria_rodriguez.png",
  quote: "Their one-on-one advisory sessions helped us navigate complex challenges with confidence. The actionable roadmap they created became our blueprint for sustainable growth."
}];
export default function Testimonials() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.15
  });
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  useEffect(() => {
    if (!api || isPaused) return;
    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [api, isPaused]);
  return <section ref={ref} className={`py-16 px-4 sm:px-6 lg:px-8 bg-background transition-all duration-300 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-foreground">Client Testimonials</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Hear from businesses we've helped achieve sustainable growth and
            operational excellence
          </p>
        </div>

        <Carousel setApi={setApi} opts={{
        align: "center",
        loop: true
      }} className="mx-auto max-w-5xl" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
          <CarouselContent>
            {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i}>
                <Card className={`card mx-2 transition-all duration-300 ease-out ${current === i ? "opacity-100 scale-100" : "opacity-70 scale-95"}`}>
                  <CardContent className="flex flex-col md:flex-row items-start gap-6 p-8">
                    <div className="flex-shrink-0">
                      <SafeHydrate><Avatar className="size-24 ring-2 ring-border">
                        <AvatarImage src={testimonial.photo} alt={testimonial.name} />
                        <AvatarFallback className="bg-primary text-foreground-primary text-2xl font-semibold">
                          {testimonial.name.split(" ").map(n => n[0]).join("")}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                    </div>

                    <div className="flex-1 space-y-4">
                      <blockquote className={`text-lg leading-relaxed ${i === 2 ? "text-black" : "text-foreground"} transition-opacity duration-300 ${current === i ? "opacity-100" : "opacity-0"}`}>
                        "{testimonial.quote}"
                      </blockquote>
                      <div className="flex flex-col">
                        <cite className={`not-italic font-semibold ${i === 2 ? "text-black" : "text-foreground"}`}>
                          {testimonial.name}
                        </cite>
                        <span className={`text-sm ${i === 2 ? "text-black" : "text-muted-foreground"}`}>
                          {testimonial.role}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>)}
          </CarouselContent>
          <CarouselPrevious className="hover-lift transition-transform duration-300 ease-out" />
          <CarouselNext className="hover-lift transition-transform duration-300 ease-out" />
        </Carousel>

        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, i) => <button key={i} data-index={i} onClick={() => api?.scrollTo(i)} className={`size-2 rounded-full transition-all duration-300 ${current === i ? "bg-primary w-8" : "bg-border hover:bg-border-strong"}`} aria-label={`Go to testimonial ${i + 1}`} />)}
        </div>
      </div>
    </section>;
}